import * as logger from "../utils/logger";
import { getAppType, AppType } from "../utils/native/nativeHelpers";
import type Api from "./api";
import type { UserService } from "../utils/users";
import type { IApiError } from "../utils/api.interfaces";

/**
 * Service to handle "Rate This App" prompts.
 * Reads showRateThisApp from user and triggers native review dialog.
 */
export class RateAppService {

    /**
     * Check if user should be prompted and trigger native review.
     * Safe to call from any environment (web, native, unsupported).
     * @param user The current user to check showRateThisApp flag
     * @param api API instance for saving user field after review
     * @param userService User service for getting current user
     */
    checkAndPromptReview(user: DeepReadonlyObject<IUser> | null, api?: Api, userService?: UserService): void {
        // When showRateThisApp is false, do nothing
        if (!user?.showRateThisApp) {
            logger.log(logger.Source.SERVICES, "RateAppService: showRateThisApp is false, skipping");
            return;
        }

        logger.log(logger.Source.SERVICES, "RateAppService: showRateThisApp is true, attempting to show review prompt");

        this.triggerNativeReview()
            .then((shown) => {
                if (shown) {
                    logger.log(logger.Source.SERVICES, "RateAppService: Review prompt shown successfully");
                    // Reset flag so user isn't prompted again
                    if (api && userService) {
                        this.resetShowRateThisApp(api, userService);
                    }
                }
            })
            .catch((error) => {
                // Error handling for native layer errors
                logger.error(logger.Source.SERVICES, `RateAppService: Failed to show review prompt - ${ error?.message || error }`);
            });
    }

    /**
     * Trigger the native app review dialog.
     * Handles unsupported platforms gracefully (no-op).
     */
    private triggerNativeReview(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const appType = getAppType();

            switch (appType) {
                case AppType.NativeSdk:
                    this.triggerNativeSdkReview(resolve, reject);
                    break;

                case AppType.CordovaApp:
                    // Cordova is deprecated - treat as unsupported
                    logger.log(logger.Source.SERVICES, "RateAppService: Cordova deprecated, skipping review prompt");
                    resolve(false);
                    break;

                case AppType.WebApp:
                default:
                    // Service smoothly handles web version
                    logger.log(logger.Source.SERVICES, "RateAppService: Web environment, skipping review prompt");
                    resolve(false);
                    break;
            }
        });
    }

    private triggerNativeSdkReview(resolve: (shown: boolean) => void, reject: (error: Error) => void): void {
        // Check if review module exists
        if (!window.geotabModules?.review) {
            logger.log(logger.Source.SERVICES, "RateAppService: review module not available");
            resolve(false); // Graceful no-op, not an error
            return;
        }

        window.geotabModules.review.request(null, (error, result) => {
            if (error) {
                reject(error);
            } else {
                logger.log(logger.Source.SERVICES, `RateAppService: Native review result - ${ result }`);
                resolve(true);
            }
        });
    }

    /**
     * Reset showRateThisApp to false after review prompt is shown.
     * Uses Get-then-Set pattern since the API does not support partial updates.
     */
    private resetShowRateThisApp(api: Api, userService: UserService): void {
        const user = userService.getCurrentUser();
        if (!user?.id) {
            return;
        }

        api.call(
            "Get",
            { typeName: "User", search: { id: user.id } },
            (users: IUser[]) => {
                const freshUser = users?.[0];
                if (freshUser) {
                    api.call(
                        "Set",
                        { typeName: "User", entity: { ...freshUser, showRateThisApp: false } },
                        () => {
                            logger.log(logger.Source.SERVICES, "RateAppService: showRateThisApp reset to false");
                        },
                        (error: IApiError) => {
                            logger.error(logger.Source.SERVICES, `RateAppService: Failed to reset showRateThisApp - ${ error.message }`);
                        }
                    );
                }
            },
            (error: IApiError) => {
                logger.error(logger.Source.SERVICES, `RateAppService: Failed to get user for reset - ${ error.message }`);
            }
        );
    }
}

export const rateAppService = new RateAppService();
